import CHOICES from "CHOICES";
import { mutate } from "utils/relay";
import {
  AUTH_TOKEN,
  DEFAULT_IATA_ID,
  DEFAULT_IATA_NUMBER,
  ENV_MAPPER,
  IATA_LIST,
  REFRESH_TOKEN,
  SELECTED_ORGANIZATION,
  TOKEN_TTL,
  DEFAULT_IATA_CURRENCY,
  DEFAULT_IATA_REFERENCE,
  NDC_CONNECT_ENDPOINT,
} from "utils/constants";
import {
  decodeReadStringifyObj,
  emptyProxyObject,
  getEnv,
  getSessionStorage,
  ProxyObjectType,
} from "utils/utils";
import { Action, Thunk, Computed, thunk, action, computed } from "easy-peasy";
import { Cookies } from "react-cookie";
import moment from "moment";
import { graphqlRequest, setAuthRefreshHandler } from "api/client";

const cookie = new Cookies();
const env = getEnv();

const cookieDomain = (env: string) =>
  env === ENV_MAPPER.production
    ? ".v3.nuflights.com"
    : env === ENV_MAPPER.connect
      ? ".connect.nuflights.com"
      : env === ENV_MAPPER.development
        ? `.dev.nuflights.com`
        : `.staging.llc.nuflights.com`;

const UK = CHOICES.UserKind;

export interface CurrentUserModel {
  username: string;
  email: string;
}

export interface SelectedOrganization {
  group?: string;
  group_name?: string;
  is_direct_member?: boolean;
  roles?: string[];
  permissions?: string[];
  [key: string]: any;
}

export type IdpTokenResponse = {
  accessToken: string;
  tokenType?: string;
  expiresIn?: number;
};

export interface IdpJwtTokenResponse {
  idpJwtToken: any;
}

export interface AuthModel {
  currentUser: CurrentUserModel | ProxyObjectType;
  authToken: string | null;
  publicKey: string | null;
  iatas: string | null;
  validUserIata: boolean;
  defaultIata: string | null;
  defaultIataID: string | null;
  isLoggedIn: Computed<AuthModel, boolean>;
  isAdmin: Computed<AuthModel, boolean>;
  setAuthToken: Action<AuthModel, string | null>;
  setValidUserIata: Action<AuthModel, string | boolean>;
  setIataNumber: Action<AuthModel, string | null>;
  setDefaultIataNumber: Action<AuthModel, string | null>;
  setDefaultIataID: Action<AuthModel, string | null>;
  setCurrentUser: Action<AuthModel, CurrentUserModel | ProxyObjectType>;
  updateAuthToken: Thunk<AuthModel, any>;
  setDefaultIataNumbers: Thunk<AuthModel, string>;
  clearAuthToken: Thunk<AuthModel>;
  fetchAuthToken: Thunk<AuthModel, any>;
  initRefreshToken: Thunk<AuthModel, boolean>;
  refreshIntervalId: NodeJS.Timeout | null;
  setRefreshIntervalId: Action<AuthModel, NodeJS.Timeout | null>;
  permissions: any;
  setPermissions: Action<AuthModel, any>;
  updatePermissions: Thunk<AuthModel, string[] | string | undefined>;
  refreshToken: string | null;
  selectedOrganization: SelectedOrganization | null;
  setSelectedOrganization: Action<AuthModel, SelectedOrganization | null>;
  updateSelectedOrganization: Thunk<AuthModel, SelectedOrganization | null>;
  setRefreshToken: Action<AuthModel, string | null>;
  updateRefreshToken: Thunk<AuthModel, any>;
  clearRefreshToken: Thunk<AuthModel>;
  setPublicKey: Thunk<any>;
  clearPublicKey: Thunk<any>;
  defaultIataCurrency: string | null;
  setDefaultIataCurrency: Action<AuthModel, string | null>;
  setIATAREFERENCE: Action<AuthModel, string | null>;
  clearIatas: Action<AuthModel>;
}

const AuthStore: AuthModel = {
  validUserIata: false,
  permissions: {},
  currentUser: emptyProxyObject,
  // authToken: cookie.get(AUTH_TOKEN),
  authToken: JSON.parse(sessionStorage.getItem(AUTH_TOKEN) || "null"),
  publicKey: cookie.get("publicKey"),
  iatas: getSessionStorage(IATA_LIST),
  defaultIata: window.sessionStorage.getItem(DEFAULT_IATA_NUMBER),
  defaultIataID: window.sessionStorage.getItem(DEFAULT_IATA_ID),
  defaultIataCurrency: window.sessionStorage.getItem(DEFAULT_IATA_CURRENCY),
  refreshToken: cookie.get(REFRESH_TOKEN),
  selectedOrganization: decodeReadStringifyObj(SELECTED_ORGANIZATION) ?? null,
  refreshIntervalId: null,

  isLoggedIn: computed((state: any) => state.currentUser !== emptyProxyObject),
  isAdmin: computed((state: any) => state.currentUser.kind === UK.ADMIN),

  setAuthToken: action((state: any, authToken) => {
    state.authToken = authToken;
  }),

  setRefreshToken: action((state: any, refreshToken) => {
    state.refreshToken = refreshToken;
  }),

  setSelectedOrganization: action((state: any, selectedOrganization) => {
    state.selectedOrganization = selectedOrganization;
  }),

  // Normally the SELECTED_ORGANIZATION cookie is set by the external home/IDP app
  // during interactive login. This thunk lets a caller that already has org data
  // from elsewhere (e.g. decoded off a plugin-issued token) establish it directly.
  updateSelectedOrganization: thunk((actions, selectedOrganization) => {
    getEnv() !== ENV_MAPPER.local
      ? cookie.set(SELECTED_ORGANIZATION, selectedOrganization, {
          domain: cookieDomain(getEnv()),
          path: "/",
          sameSite: "strict",
          secure: true,
        })
      : cookie.set(SELECTED_ORGANIZATION, selectedOrganization, {
          path: "/",
          sameSite: "strict",
          secure: true,
        });
    actions.setSelectedOrganization(selectedOrganization);
    actions.updatePermissions(selectedOrganization?.permissions);
  }),

  setValidUserIata: action((state: any, currentState: boolean) => {
    state.validUserIata = Boolean(currentState);
  }),

  setPermissions: action((state: any, permissions) => {
    state.permissions = permissions;
  }),

  setIataNumber: action((state: any, iatas: string) => {
    const localIatas = JSON.parse(iatas) || [];
    const iataNumber =
      localIatas?.find((edge: any) => edge.node.isDefault)?.node?.number ||
      localIatas[0]?.node?.number ||
      null;
    const iataID = localIatas.find(
      (iata: any) => iata?.node?.number === iataNumber,
    )?.node?.id;
    window.sessionStorage.setItem(DEFAULT_IATA_NUMBER, iataNumber);
    window.sessionStorage.setItem(IATA_LIST, String(iatas));
    state.iatas = JSON.parse(iatas);
    state.defaultIata = iataNumber;
    state.defaultIataID = iataID;
  }),

  setCurrentUser: action((state: any, currentUser) => {
    state.currentUser = currentUser;
  }),

  setRefreshIntervalId: action((state: any, intervalId) => {
    state.refreshIntervalId = intervalId;
  }),

  updatePermissions: thunk((actions, permissions) => {
    const permissionsObj = permissions ? permissions : [];
    // const perms: Record<string, boolean> = {};
    // permissions?.forEach((p) => (perms[p] = true));
    actions.setPermissions(permissionsObj);
  }),

  updateAuthToken: thunk((actions, authToken) => {
    getEnv() !== ENV_MAPPER.local
      ? cookie.set(AUTH_TOKEN, authToken.self, {
          domain: cookieDomain(getEnv()),
          path: "/",
          sameSite: "strict",
          secure: true,
          expires:
            authToken.expiry instanceof Date
              ? authToken.expiry
              : new Date(authToken.expiry),
        })
      : cookie.set(AUTH_TOKEN, authToken.self, {
          path: "/",
          sameSite: "strict",
          secure: true,
        });
    actions.setAuthToken(authToken.self);
  }),

  updateRefreshToken: thunk((actions, refreshToken) => {
    getEnv() !== ENV_MAPPER.local
      ? cookie.set(REFRESH_TOKEN, refreshToken.self, {
          domain: cookieDomain(getEnv()),
          path: "/",
          sameSite: "strict",
          secure: true,
          expires:
            refreshToken.expiry instanceof Date
              ? refreshToken.expiry
              : new Date(refreshToken.expiry),
        })
      : cookie.set(REFRESH_TOKEN, refreshToken.self, {
          path: "/",
          sameSite: "strict",
          secure: true,
        });
    actions.setRefreshToken(refreshToken.self);
  }),

  setDefaultIataNumber: action((state: any, iataNumber: string) => {
    state.defaultIata = iataNumber;
    window.sessionStorage.setItem(DEFAULT_IATA_NUMBER, iataNumber);
  }),

  setDefaultIataID: action((state: any, iataID: string) => {
    state.defaultIataID = iataID;
    window.sessionStorage.setItem(DEFAULT_IATA_ID, iataID);
  }),

  setDefaultIataNumbers: thunk((actions, iatas) => {
    actions.setIataNumber(iatas);
  }),

  setDefaultIataCurrency: action((state: any, iataCurrency: string) => {
    state.defaultIataCurrency = iataCurrency;
    window.sessionStorage.setItem(DEFAULT_IATA_CURRENCY, iataCurrency);
  }),

  setIATAREFERENCE: action((state: any, iataReference: string) => {
    state.defaultIataReference = iataReference;
    window.sessionStorage.setItem(DEFAULT_IATA_REFERENCE, iataReference);
  }),

  clearAuthToken: thunk((actions, payload, { getState }) => {
    const state = getState();
    if (state.refreshIntervalId) {
      clearInterval(state.refreshIntervalId);
      actions.setRefreshIntervalId(null);
    }

    window.localStorage.clear();
    window.sessionStorage.clear();
    env !== ENV_MAPPER.local
      ? cookie.remove(AUTH_TOKEN, {
          domain: cookieDomain(env),
          path: "/",
        })
      : cookie.remove(AUTH_TOKEN, { path: "/" });
    actions.setAuthToken(null);
    actions.clearPublicKey();
    actions.setCurrentUser(emptyProxyObject);
    actions.setPermissions([]);
  }),

  clearRefreshToken: thunk((actions, payload, { getState }) => {
    const state = getState();
    if (state.refreshIntervalId) {
      clearInterval(state.refreshIntervalId);
      actions.setRefreshIntervalId(null);
    }

    window.localStorage.clear();
    getEnv() !== ENV_MAPPER.local
      ? cookie.remove(REFRESH_TOKEN, {
          domain: cookieDomain(getEnv()),
          path: "/",
        })
      : cookie.remove(REFRESH_TOKEN, { path: "/" });
    actions.setRefreshToken(null);
    actions.setCurrentUser(emptyProxyObject);
  }),

  setPublicKey: thunk((_actions, publicKey) => {
    getEnv() !== ENV_MAPPER.local
      ? cookie.set("publicKey", publicKey, {
          domain: cookieDomain(getEnv()),
          path: "/",
        })
      : cookie.set("publicKey", publicKey, { path: "/" });
  }),

  clearPublicKey: thunk(() => {
    getEnv() !== ENV_MAPPER.local
      ? cookie.remove("publicKey", {
          domain: cookieDomain(getEnv()),
          path: "/",
        })
      : cookie.remove("publicKey", { path: "/" });
  }),

  fetchAuthToken: thunk(
    (actions, { mutation, input, onSuccess, onFailure }) => {
      mutate({
        mutation,
        input,
        onSuccess: function (data: any) {
          let iatas;
          let token: any = {
            self: "",
            expiry: new Date(),
          };
          let refreshToken: any = {
            self: "",
            expiry: new Date(),
          };
          let unixTime = Number(moment.unix(data.login.refreshExpiresIn || 0));
          let date = new Date(unixTime);
          if (data.login) {
            token.self = data.login.token;
            token.expiry = date;
            refreshToken.self = data.login.refreshToken;
            refreshToken.expiry = date;
            iatas = JSON.stringify(data.login.user.iatas.edges);
          } else if (data.socialAuth) {
            token = data.socialAuth.token;
          }
          actions.updateAuthToken(token);
          actions.updateRefreshToken(refreshToken);
          actions.setDefaultIataNumbers(String(iatas));
          onSuccess(data);
        },
        onFailure: function (messages: []) {
          actions.clearAuthToken();
          actions.clearRefreshToken();
          onFailure(messages);
        },
      });
    },
  ),

  initRefreshToken: thunk((actions, initialFetch = true, { getState }) => {
    const refreshAccessToken = async () => {
      try {
        const query = `
          mutation idpRefresh {
            idpRefresh {
              accessToken
              tokenType
              expiresIn
            }
          }
        `;

        const data = await graphqlRequest<{ idpRefresh: IdpTokenResponse }>(
          query,
          undefined,
          undefined,
          NDC_CONNECT_ENDPOINT,
        );

        const accessToken = data.idpRefresh.accessToken;
        const expiresIn = data.idpRefresh.expiresIn ?? 300;
        const expiry = new Date(Date.now() + expiresIn * 1000);

        actions.updateAuthToken({ self: accessToken, expiry });

        const selectedOrganization =
          decodeReadStringifyObj(SELECTED_ORGANIZATION) ?? null;
        actions.setSelectedOrganization(selectedOrganization);
        actions.updatePermissions(selectedOrganization?.permissions);

        return true;
      } catch (error) {
        console.log("failed in idpRefresh token flow", error);
        actions.clearAuthToken();
        actions.clearRefreshToken();
        return false;
      }
    };

    setAuthRefreshHandler(refreshAccessToken);

    if (initialFetch) {
      void refreshAccessToken();
    }

    const state = getState();
    if (state.refreshIntervalId) {
      clearInterval(state.refreshIntervalId);
      actions.setRefreshIntervalId(null);
    }

    const intervalId = setInterval(() => {
      void refreshAccessToken();
    }, TOKEN_TTL);

    actions.setRefreshIntervalId(intervalId);
  }),

  clearIatas: action((state) => {
    state.iatas = null;
  }),
};

export default AuthStore;
